import { supabase } from './supabase';
import { escapeLike } from './tropeUtils';


/**
 * @param {object} book - must have title + author_name; isbn13/isbn optional
 * @param {object} opts
 * @param {string} opts.submission_type - 'reader' | 'csv' | 'author' etc.
 * @param {object} opts.extra - additional columns to set only on insert
 * @param {object} opts.fillIfNull - pass this whenever a real book-search
 *   response was actually received for this book, even if both fields
 *   came back null. Presence, not content, is the signal: passing it
 *   means "we checked" and calls fill_book_popularity_if_null(), a
 *   SECURITY DEFINER RPC (needed because "Update own books" RLS blocks a
 *   plain client UPDATE on a book the caller did not submit -- the
 *   common case when a search re-matches someone else's book). The RPC
 *   fills ratings_count/edition_count only where each is currently null,
 *   but ALWAYS stamps popularity_checked_at when called -- omitting it
 *   here on a genuine "asked, got nothing" would make that book
 *   indistinguishable from one that was never checked at all, the exact
 *   failure-looks-like-absence bug this column exists to prevent. Pass
 *   null (the default) only when no search happened for this book at
 *   this call -- a platform pick or a fully manual entry, for instance.
 *   See supabase/migrations/20260902140000_add_popularity_signal_columns.sql.
 * @param {number|null} [opts.fillIfNull.ratings_count]
 * @param {number|null} [opts.fillIfNull.edition_count]
 * @returns {Promise<string|null>} book id, existing or newly created
 */
/**
 * Same as findOrCreateBook, but returns the outcome instead of only the id.
 *
 * findOrCreateBook keeps its scalar `id | null` return because five of its six
 * call sites only ever want the id and changing all of them to satisfy one
 * would be a much wider diff than this bug needs. Use this variant wherever
 * the caller SHOWED THE READER A SUCCESS — it is the only way to tell an
 * honest one from a lie.
 *
 * @returns {Promise<{id: string|null, filled: string[], fillError: string|null}>}
 *   id        — the book, existing or newly created
 *   filled    — fields written onto an existing row, [] when nothing needed it
 *   fillError — non-null ONLY when a fill was attempted and failed. `filled`
 *               empty with `fillError` null means "asked, nothing to do";
 *               those two must never be confused, which is the whole point.
 */
export async function findOrCreateBookDetailed(book, opts = {}) {
  const out = { id: null, filled: [], fillError: null };
  out.id = await findOrCreateBook(book, { ...opts, _report: out });
  return out;
}

export async function findOrCreateBook(book, { submission_type = 'reader', extra = {}, fillIfNull = null, _report = null } = {}) {
  // Complete an EXISTING row from what the reader just supplied.
  //
  // The bug this fixed: a reader uploaded a cover for a book already in the
  // catalogue (the homepage add-a-book form, removed in ticket 23, put the
  // file in the `covers` bucket and handed the URL in here). The match below
  // returns the existing id, and before this existed nothing ever wrote that
  // URL anywhere — the file uploaded, the reader saw success, the book stayed
  // coverless, and nothing reported it.
  //
  // Goes through an RPC rather than a client .update() for the same reason
  // fillPopularityIfMatched does: "Update own books" RLS
  // (live_schema_snapshot.sql:5240) blocks an UPDATE on a book the caller did
  // not submit, which is exactly this case. See migration 20260904000000.
  //
  // Fill-if-blank only, enforced server-side — a reader completes a gap and can
  // never overwrite a value somebody else supplied.
  async function fillDetailsIfMatched(matchedId) {
    // Blank-aware, matching the server's is_blank(): a value of only spaces,
    // tabs or a pasted non-breaking space is nothing to offer, so normalise it
    // to null here rather than shipping it.
    //
    // Rohberto's review: the first version tested plain JS truthiness, so a
    // whitespace-only title was "offered", made the round trip, and came back
    // not_offered from the server's own is_blank() check. Correct outcome,
    // wasted call. Trimming the same characters the SQL side trims means the
    // two agree before the request rather than after it.
    const offerable = (v) => {
      const t = (v ?? '').replace(/[\u00a0\u202f\ufeff]/g, ' ').trim();
      return t ? v : null;
    };
    const offered = {
      p_cover_image_url: offerable(book.cover_image_url),
      p_title:           offerable(book.title),
      p_author_name:     offerable(author_name),
    };
    // Nothing to offer means nothing to report. Not an error, and not a
    // silent skip either — there is simply no write to attempt.
    if (!offered.p_cover_image_url && !offered.p_title && !offered.p_author_name) return;

    // `data: fillData, error: fillDetailErr` rather than shorthand `{ data, error }`:
    // every other destructure in this file names them, and check-silent-writes
    // only recognises the named form as a checked write.
    const { data: fillData, error: fillDetailErr } = await supabase.rpc('fill_book_details_if_null', {
      p_book_id: matchedId, ...offered,
    });
    if (fillDetailErr) {
      // Reported, not swallowed. A caller using findOrCreateBookDetailed can
      // roll back the success it already showed; console.error alone would
      // reproduce the exact bug this function exists to remove.
      console.error('findOrCreateBook: detail fill-if-null failed:', fillDetailErr.message);
      if (_report) _report.fillError = fillDetailErr.message;
      return;
    }
    if (_report) _report.filled = Array.isArray(fillData?.filled) ? fillData.filled : [];
  }

  async function fillPopularityIfMatched(matchedId) {
    // fillIfNull's mere presence means "a real check happened" -- see the
    // JSDoc above. Do not gate this on ratings_count/edition_count being
    // non-null: a genuine "asked, source has nothing" still has to move
    // popularity_checked_at, or it is indistinguishable from "never asked".
    if (!fillIfNull) return;
    const { error: fillErr } = await supabase.rpc('fill_book_popularity_if_null', {
      p_book_id: matchedId,
      p_ratings_count: fillIfNull.ratings_count ?? null,
      p_edition_count: fillIfNull.edition_count ?? null,
    });
    // Non-fatal by design: the book id is already resolved and usable even
    // if this fails (RLS denial, matched id gone, etc). Logged so a silent
    // failure here does not read as "the columns are being filled".
    if (fillErr) console.error('findOrCreateBook: popularity fill-if-null failed:', fillErr.message);
  }

  const isbn13 = normalizeIsbn(book.isbn13 || book.isbn);
  const author_name = book.author_name || book.author || null;

  if (!book.title || !author_name) {
    console.error('findOrCreateBook: missing title or author_name', book);
    return null;
  }

  try {
      // 1. Match on ISBN first
    if (isbn13) {
      const { data: byIsbn, error: isbnErr } = await supabase
        .from('books')
        .select('id')
        .eq('isbn13', isbn13)
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (isbnErr) {
        console.error('findOrCreateBook ISBN lookup error:', isbnErr.message);
        return null; // inconclusive — do not fall through to a less accurate match
      }
      if (byIsbn?.id) { await fillDetailsIfMatched(byIsbn.id); await fillPopularityIfMatched(byIsbn.id); return byIsbn.id; }
    }

    // 2. Fall back to fuzzy title + author
    const { data: byTitleAuthor, error: taErr } = await supabase
      .from('books')
      .select('id')
      .ilike('title', escapeLike(book.title))
      .ilike('author_name', escapeLike(author_name))
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (taErr) console.error('findOrCreateBook title/author lookup error:', taErr.message);
    if (byTitleAuthor?.id) { await fillDetailsIfMatched(byTitleAuthor.id); await fillPopularityIfMatched(byTitleAuthor.id); return byTitleAuthor.id; }

    // 3. Not found anywhere — insert
    const { data: newBook, error: insertErr } = await supabase
      .from('books')
      .insert({
        title: book.title,
        author_name,
        cover_image_url: book.cover_image_url || null,
        isbn13,
        status: 'pending',
        submission_type,
        ...extra,
      })
      .select('id')
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (insertErr) {
      console.error('findOrCreateBook insert error:', insertErr.message);
      return null;
    }
    return newBook?.id || null;
  } catch (err) {
    console.error('findOrCreateBook failed:', err.message);
    return null;
  }
}

export function normalizeIsbn(v) {
  if (!v) return null;
  const cleaned = String(v).replace(/[^0-9Xx]/g, '').toUpperCase();
  return cleaned.length >= 10 ? cleaned : null;
}

